// International workspace — shared presentational kit. Mirrors the Basketball
// _bbkit pattern: section taxonomy, icons, primitives (IntlHead, Hero block,
// AwaitPanel, Loading, StatusPill, Seam, Scoreboard tiles, useRpc) and the
// shared formatters. All styling lives under the `.intl` scoped layer in
// styles/international.css — never touch sidebar/topbar primitives.
//
// Palette: deep international-navy + tournament-gold (distinct from
// Basketball's orange/wood). Display font is Bebas Neue, same as Basketball,
// so workspaces feel like siblings rather than strangers.

import { type CSSProperties, type ReactNode } from "react";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

// ── Section taxonomy ──────────────────────────────────────────────
export type IntlSection =
  | "dashboard" | "signals" | "wc-hub" | "rounds" | "clv" | "no-bets";

export interface SectionMeta { crumb: string; kicker: string; title: string }
export const sectionMeta: Record<IntlSection, SectionMeta> = {
  dashboard: { crumb: "Dashboard",       kicker: "Dashboard",      title: "International" },
  signals:   { crumb: "Signals",         kicker: "Signals",        title: "International" },
  "wc-hub":  { crumb: "WC 2026 Hub",     kicker: "WC 2026",        title: "FIFA World Cup 2026" },
  rounds:    { crumb: "Rounds",          kicker: "Rounds",         title: "Per-round predictions" },
  clv:       { crumb: "CLV Track",       kicker: "CLV Track",      title: "International" },
  "no-bets": { crumb: "No-Bet Report",   kicker: "No-Bet Report",  title: "International" },
};

// ── Utils + formatters ────────────────────────────────────────────
export const DASH = "—";
export function fmtOdds(o: number | string | null | undefined): string {
  if (typeof o === "string") return o.trim() || DASH;
  return typeof o === "number" && o > 0 ? o.toFixed(2) : DASH;
}
export function fmtPct(n: number | null | undefined, signed = false): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return DASH;
  const v = n.toFixed(1);
  return signed && n > 0 ? `+${v}%` : `${v}%`;
}
export function fmtProb(p: number | null | undefined): string {
  return typeof p === "number" && Number.isFinite(p) ? `${(p * 100).toFixed(1)}%` : DASH;
}
export function fmtKick(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleTimeString("en-GH", {
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Africa/Accra",
  }) + " GMT";
}
export function fmtDateOnly(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}
export function daysUntil(target: Date): number {
  return Math.max(0, Math.ceil((target.getTime() - Date.now()) / 86_400_000));
}

// ── Icons (scoped to International) ───────────────────────────────
export function IIcon({ name, size = 16, color = "currentColor" }: { name: string; size?: number; color?: string }): JSX.Element {
  const c = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const p: Record<string, ReactNode> = {
    globe: (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a13 13 0 0 1 0 18" /><path d="M12 3a13 13 0 0 0 0 18" /></>),
    trophy: (<><path d="M6 4h12v4a6 6 0 0 1-12 0Z" /><path d="M6 6H3v2a3 3 0 0 0 3 3M18 6h3v2a3 3 0 0 1-3 3" /><path d="M10 14h4v3l2 3H8l2-3z" /></>),
    chev: <path d="m9 6 6 6-6 6" />,
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
    target: (<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" fill={color} /></>),
    flag: (<><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></>),
    medal: (<><circle cx="12" cy="15" r="6" /><path d="M9 9 6 3h12l-3 6" /></>),
    bracket: (<><path d="M5 5h3v14H5z" /><path d="M16 5h3v14h-3z" /><path d="M8 12h2M14 12h2" /></>),
    chart: (<><path d="M3 17l6-6 4 4 8-8" /><path d="M21 7h-5M21 7v5" /></>),
    decline: (<><circle cx="12" cy="12" r="9" /><path d="m8 8 8 8M16 8l-8 8" /></>),
    info: (<><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></>),
  };
  return <svg {...c}>{p[name] ?? null}</svg>;
}

export function IntlMark({ size = 20 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 12h18" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ── Country flags ────────────────────────────────────────────────
// FIFA 3-letter → ISO 3166-1 alpha-2 (lowercase) for flagcdn.com.
const FIFA_TO_ISO2: Record<string, string> = {
  AFG:"af",ALB:"al",ALG:"dz",AND:"ad",ANG:"ao",ARG:"ar",ARM:"am",
  AUS:"au",AUT:"at",AZE:"az",BAH:"bs",BHR:"bh",BAN:"bd",BRB:"bb",
  BLR:"by",BEL:"be",BLZ:"bz",BEN:"bj",BER:"bm",BHU:"bt",BOL:"bo",
  BIH:"ba",BOT:"bw",BRA:"br",BRU:"bn",BUL:"bg",BFA:"bf",BDI:"bi",
  CAM:"kh",CMR:"cm",CAN:"ca",CPV:"cv",CAY:"ky",CTA:"cf",CHA:"td",
  CHI:"cl",CHN:"cn",COL:"co",COM:"km",CGO:"cg",COD:"cd",CRC:"cr",
  CIV:"ci",CRO:"hr",CUB:"cu",CYP:"cy",CZE:"cz",DEN:"dk",DJI:"dj",
  DOM:"do",ECU:"ec",EGY:"eg",SLV:"sv",EQG:"gq",ERI:"er",EST:"ee",
  ETH:"et",FIJ:"fj",FIN:"fi",FRA:"fr",GAB:"ga",GAM:"gm",GEO:"ge",
  GER:"de",GHA:"gh",GRE:"gr",GUA:"gt",GUI:"gn",GNB:"gw",GUY:"gy",
  HAI:"ht",HON:"hn",HKG:"hk",HUN:"hu",ISL:"is",IND:"in",IDN:"id",
  IRN:"ir",IRQ:"iq",IRL:"ie",ISR:"il",ITA:"it",JAM:"jm",JPN:"jp",
  JOR:"jo",KAZ:"kz",KEN:"ke",KOR:"kr",KUW:"kw",KGZ:"kg",LAO:"la",
  LVA:"lv",LBN:"lb",LES:"ls",LBR:"lr",LBY:"ly",LIE:"li",LTU:"lt",
  LUX:"lu",MAC:"mo",MAD:"mg",MWI:"mw",MAS:"my",MDV:"mv",MLI:"ml",
  MLT:"mt",MTN:"mr",MRI:"mu",MEX:"mx",MDA:"md",MNG:"mn",MNE:"me",
  MAR:"ma",MOZ:"mz",MYA:"mm",NAM:"na",NEP:"np",NED:"nl",NZL:"nz",
  NCA:"ni",NIG:"ne",NGA:"ng",MKD:"mk",NOR:"no",OMA:"om",PAK:"pk",
  PLE:"ps",PAN:"pa",PNG:"pg",PAR:"py",PER:"pe",PHI:"ph",POL:"pl",
  POR:"pt",QAT:"qa",ROU:"ro",RUS:"ru",RWA:"rw",KSA:"sa",SEN:"sn",
  SRB:"rs",SLE:"sl",SGP:"sg",SVK:"sk",SVN:"si",SOM:"so",RSA:"za",
  ESP:"es",SRI:"lk",SDN:"sd",SUR:"sr",SWZ:"sz",SWE:"se",SUI:"ch",
  SYR:"sy",THA:"th",TLS:"tl",TOG:"tg",TRI:"tt",TUN:"tn",TUR:"tr",
  TKM:"tm",UGA:"ug",UKR:"ua",UAE:"ae",ENG:"gb-eng",SCO:"gb-sct",
  WAL:"gb-wls",NIR:"gb-nir",USA:"us",URU:"uy",UZB:"uz",VEN:"ve",
  VIE:"vn",YEM:"ye",ZAM:"zm",ZIM:"zw",KVX:"xk",CUW:"cw",
  GRN:"gd",SKN:"kn",LCA:"lc",VIN:"vc",SMA:"ws",TAH:"pf",NCL:"nc",
  PRK:"kp",TPE:"tw",
};

export function fifaToIso2(fifaCode: string | null | undefined): string | null {
  if (!fifaCode) return null;
  return FIFA_TO_ISO2[fifaCode.toUpperCase()] ?? null;
}

export function NationFlag({ code, size = 20 }: { code: string | null | undefined; size?: number }): JSX.Element | null {
  const iso2 = fifaToIso2(code);
  if (!iso2) return null;
  return (
    <img
      className="intl-flag"
      src={`https://flagcdn.com/w40/${iso2}.png`}
      srcSet={`https://flagcdn.com/w80/${iso2}.png 2x`}
      alt={code ?? ""}
      width={size}
      height={Math.round(size * 0.75)}
      loading="lazy"
    />
  );
}

// ── Page chrome ───────────────────────────────────────────────────
export function IntlHead({
  kicker, title, status = "Workspace · live", live = false,
}: { kicker: string; title: string; status?: string; live?: boolean }): JSX.Element {
  return (
    <div className="intl-kicker-rail">
      <div className="intl-mark"><IntlMark size={20} /></div>
      <div className="intl-title-stack">
        <div className="intl-kicker">
          <span>International</span><span className="sep">·</span><span className="acc">{kicker}</span>
        </div>
        <div className="intl-title">{title}</div>
      </div>
      <div className={`intl-status${live ? " live" : ""}`}>
        <span className="pulse" /><span>{status}</span>
      </div>
    </div>
  );
}

export function AwaitPanel({
  kicker = "CALIBRATING", title, body, ghosts = 5, icon = "clock",
}: { kicker?: string; title: string; body: string; ghosts?: number; icon?: string }): JSX.Element {
  return (
    <div className="intl-await">
      <div className="intl-await-mark"><IIcon name={icon} size={24} /></div>
      <div className="intl-await-body">
        <div className="k">{kicker}</div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
      <div className="intl-await-ghosts">
        {Array.from({ length: ghosts }).map((_, i) => <div key={i} className="g" />)}
      </div>
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }): JSX.Element {
  return <div className="intl-loading"><span className="intl-loading-dot" />{label}</div>;
}

export function StatusPill({
  tone = "default", children,
}: { tone?: "default" | "live" | "calibrating" | "dormant"; children: ReactNode }): JSX.Element {
  return <span className={`intl-pill ${tone}`}>{children}</span>;
}

export function Seam({ color = "var(--border)", opacity = 1 }: { color?: string; opacity?: number }): JSX.Element {
  return (
    <div className="intl-seam" style={{ opacity }}>
      <svg width="100%" height="14" viewBox="0 0 1000 14" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 7 L 1000 7" fill="none" stroke={color} strokeWidth="1" strokeDasharray="2 6" />
      </svg>
    </div>
  );
}

// ── Hero — Dashboard headline block ───────────────────────────────
export function IntlHero({
  kicker, title, lede, chips, aside,
}: { kicker: ReactNode; title: ReactNode; lede: string; chips?: ReactNode; aside?: ReactNode }): JSX.Element {
  return (
    <div className="intl-hero">
      <div>
        <div className="intl-hero-kicker">{kicker}</div>
        <h1>{title}</h1>
        <p className="lede">{lede}</p>
        {chips ? <div className="intl-hero-chips">{chips}</div> : null}
      </div>
      {aside ? <div className="intl-hero-aside">{aside}</div> : null}
    </div>
  );
}

export function HeroChip({ children, tone = "default" }: { children: ReactNode; tone?: "default" | "accent" }): JSX.Element {
  return <span className={`intl-chip ${tone}`}><span className="d" />{children}</span>;
}

// ── Definition list (hero aside) ─────────────────────────────────
export function DefList({ rows }: { rows: Array<{ k: string; v: ReactNode; dim?: boolean }> }): JSX.Element {
  return (
    <div className="intl-deflist">
      {rows.map((r, i) => (
        <div key={i} className="intl-defrow">
          <div className="k">{r.k}</div>
          <div className={`v${r.dim ? " dim" : ""}`}>{r.v}</div>
        </div>
      ))}
    </div>
  );
}

// ── Scoreboard tile — big-number stat ─────────────────────────────
export function Scoreboard({ tiles }: { tiles: Array<{ label: string; value: ReactNode; sub?: string; live?: boolean; to?: string }> }): JSX.Element {
  return (
    <div className="intl-scoreboard">
      {tiles.map((t, i) => (
        <div key={i} className={`intl-score${t.live ? " live" : ""}`}>
          <div className="top">
            <span className={`pip${t.live ? " on" : ""}`} />
            <span className="lab">{t.label}</span>
          </div>
          <div className="big">{t.value}</div>
          {t.sub ? <div className="sub">{t.sub}</div> : null}
          <div className="baseline" />
        </div>
      ))}
    </div>
  );
}

// ── Card frame ────────────────────────────────────────────────────
export function Card({
  kicker, title, action, children, padding = true,
}: { kicker?: string; title?: ReactNode; action?: ReactNode; children: ReactNode; padding?: boolean }): JSX.Element {
  return (
    <div className="intl-card">
      {(kicker || title || action) && (
        <div className="intl-card-head">
          <div>
            {kicker ? <div className="intl-card-kicker">{kicker}</div> : null}
            {title ? <div className="intl-card-title">{title}</div> : null}
          </div>
          {action ? <div className="intl-card-action">{action}</div> : null}
        </div>
      )}
      <div className={padding ? "intl-card-body" : "intl-card-body-flush"}>{children}</div>
    </div>
  );
}

// ── Util: blend a hex shade (used by mark gradients) ─────────────
export function shade(hex: string, percent: number): string {
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) + percent;
  let g = ((num >> 8) & 0xff) + percent;
  let b = (num & 0xff) + percent;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

// ── Hook: read a no-arg jsonb RPC (mirrors useRpc in _bbkit) ─────
// Delegates to the shared data layer (useRpcQuery): cached, deduped,
// refetch-on-focus/return. Public {loading,error,data} shape preserved.
export function useRpc<T>(fn: string): { loading: boolean; error: string | null; data: T | null } {
  const q = useRpcQuery<T | null>(fn);
  return {
    loading: q.isLoading,
    error: q.error ? q.error.message : null,
    data: q.data ?? null,
  };
}

// Silence the unused-CSSProperties import if a future variant needs it; keeping
// the import lets callers spread inline styles into mark gradients without
// re-importing.
export type { CSSProperties };
